/**
 * The sink that actually sends, to this app's own API.
 *
 * First-party because a third-party tag is blocked outright for a fifth to a
 * third of visitors, and because this app is used at the bins with no signal —
 * a vendor script cannot hold events until the resident is back online, and
 * that is exactly the session worth measuring.
 *
 * Events are batched rather than sent one at a time: a page view costs nothing,
 * but a request per page view on a phone with one bar is a real delay in front
 * of the thing the resident actually opened the app for.
 */

import type { AnalyticsEvent, EventName, EventProps, Sink } from "./events";
import { APP_VERSION } from "../version";

const ENDPOINT = `${import.meta.env.VITE_API_URL || "/api"}/analytics`;

export interface FirstPartySinkOptions {
  /** How long a batch may wait before being sent. */
  flushIntervalMs?: number;
  /** Send immediately once this many events are waiting. */
  maxBatchSize?: number;
  /** Stop queueing past this, so an offline session cannot grow unbounded. */
  maxQueueSize?: number;
  /** The language to stamp on events; read lazily so it follows a change. */
  language?: () => string;
}

export class FirstPartySink implements Sink {
  private queue: AnalyticsEvent[] = [];
  private timer: ReturnType<typeof setInterval> | null = null;
  private readonly flushIntervalMs: number;
  private readonly maxBatchSize: number;
  private readonly maxQueueSize: number;
  private readonly language: () => string;

  constructor(options: FirstPartySinkOptions = {}) {
    this.flushIntervalMs = options.flushIntervalMs ?? 30_000;
    this.maxBatchSize = options.maxBatchSize ?? 50;
    this.maxQueueSize = options.maxQueueSize ?? 500;
    this.language = options.language ?? (() => "de-CH");
  }

  /**
   * Begin batching, and arrange to send what is queued before the page goes.
   *
   * `visibilitychange` rather than `unload`: a PWA on a phone is far more often
   * backgrounded than closed, and `unload` does not fire reliably on mobile at
   * all. Separate from the constructor so a sink can be built in a test without
   * attaching listeners to a document.
   */
  start(): () => void {
    const onHidden = () => {
      if (document.visibilityState === "hidden") void this.flush();
    };

    document.addEventListener("visibilitychange", onHidden);
    this.timer = setInterval(() => {
      if (this.queue.length > 0) void this.flush();
    }, this.flushIntervalMs);

    return () => {
      document.removeEventListener("visibilitychange", onHidden);
      if (this.timer !== null) clearInterval(this.timer);
      this.timer = null;
    };
  }

  track(name: EventName, props?: EventProps): void {
    // A resident offline for a long walk round the collection points must not
    // accumulate a queue that never sends. Oldest goes first: the recent
    // events are the ones still worth having.
    if (this.queue.length >= this.maxQueueSize) this.queue.shift();

    this.queue.push({
      name,
      props,
      lang: this.language(),
      app_version: APP_VERSION,
    });

    if (this.queue.length >= this.maxBatchSize) void this.flush();
  }

  /**
   * Send what is queued.
   *
   * The queue is emptied *before* the request, and a failure does not put it
   * back. Re-queueing would mean a batch the server will never accept is
   * retried forever, growing behind every new event — and analytics are not
   * worth one byte of a resident's connection more than they cost already.
   */
  async flush(): Promise<void> {
    if (this.queue.length === 0) return;

    const events = this.queue.splice(0);

    try {
      const response = await fetch(ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ events }),
        // Lets the request outlive the page it was sent from, which is the
        // whole reason the visibility handler can flush at all.
        keepalive: true,
      });

      if (!response.ok) {
        console.error(`Analytics flush failed (${response.status})`);
      }
    } catch (e) {
      console.error("Analytics flush error:", e);
    }
  }

  /** Test seam: what is waiting, without sending it. */
  pending(): readonly AnalyticsEvent[] {
    return this.queue;
  }
}
